import { useContext, useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { LangContext } from '@/context/LangProvider';
import { Textarea } from '@/components/ui/textarea';
import RequiredLabel from '@/components/labels/RequiredLabel';
import useCreateProjectStore from '@/store/CreateProjectStore';
import { DiscussionGuideProps, InterviewQuestion, InterviewQuestions } from '@/types';
import DeleteQuestionModal from '@/components/Modals/DeleteQuestionModal';
import { useDiscussionGuideHandlers, useDragAndDropHandlers } from '@/hooks';
import {
  isGroup,
  isCheckpoint,
  shouldRenderGroupUI,
  wrapQuestionsInGroup,
  flattenGroupToQuestions,
  splitIntoCheckpointBlocks,
  countQuestionsInBlock,
} from '@/utils';
import { QuestionGroupLimits } from '@/enums';
import { DiscussionGuideHeader } from './discussion-guide/DiscussionGuideHeader';
import { GroupModeView } from './discussion-guide/GroupModeView';
import { FlatModeView } from './discussion-guide/FlatModeView';

// Helper function to create a fresh initial question (deep copy)
const createInitialQuestion = (): Omit<InterviewQuestion, 'id'> => ({
  question: '',
  follow_up_question: '0',
  media: undefined,
  answer_quantification: [
    {
      quantify_answer: false,
      value: '',
    },
  ],
});

export function DiscussionGuide({
  singleProjectData,
  isProjectPublished,
  isProjectCompleted,
  isEmbedProject,
  register,
  control,
  setValue,
  clearErrors,
  setFileData,
  fileData,
  currentQuestions,
  errors,
  submitAttempt,
}: DiscussionGuideProps) {
  const { lang } = useContext(LangContext);
  const {
    setIsFormChanged,
    setReorderChanged,
    setInterviewQuestionsStructureChanged,
    uploadingQuestionId,
  } = useCreateProjectStore();
  const { isUserViewer } = useWorkspaceRoles();

  const [groupStructure, setGroupStructure] = useState<InterviewQuestions>(() => {
    if (currentQuestions) {
      return currentQuestions;
    }
    return wrapQuestionsInGroup([], 'Interview Questions', false);
  });

  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);

  // Track if we're updating internally to prevent sync loop
  const isInternalUpdate = useRef(false);

  const showGroupUI = shouldRenderGroupUI(groupStructure);
  const flatQuestions = flattenGroupToQuestions(groupStructure);

  // Interview projects use the global 40-question cap. Embed projects (TELV1-457)
  // are not flat-capped — they cap per block (≤3 questions) and on block count
  // (≤10 blocks / ≤9 checkpoints), derived below — so the header's global-limit
  // warning stays interview-only.
  const maxQuestions = QuestionGroupLimits.MAX_QUESTIONS;

  // Check if question exists in saved database
  const isQuestionInDatabase = (questionId: string): boolean => {
    if (!questionId || !singleProjectData?.interview_questions?.children) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recursive walk over mixed question / group / checkpoint nodes (TELV1-421)
    const searchInChildren = (children: any[]): boolean =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children.some((child: any) => {
        if (child.id === questionId) {
          return true;
        }
        return (
          'children' in child && Array.isArray(child.children) && searchInChildren(child.children)
        );
      });

    return searchInChildren(singleProjectData.interview_questions.children);
  };

  // Sync local state with form value when it changes from external source (e.g., reset after save)
  useEffect(() => {
    if (currentQuestions && !isInternalUpdate.current) {
      // Only sync if currentQuestions has valid structure with children array
      if (Array.isArray(currentQuestions.children)) {
        setGroupStructure(currentQuestions);
      } else if (currentQuestions.children === undefined) {
        // If children is undefined, don't sync (keep current structure)
      } else {
        // If children exists but is not an array, fix the structure
        setGroupStructure({
          ...currentQuestions,
          children: [],
        });
      }
    }
    // Reset the flag after sync
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
    }
  }, [currentQuestions]);

  // Update form value whenever group structure changes
  const updateFormValue = (newStructure: InterviewQuestions) => {
    isInternalUpdate.current = true; // Mark as internal update
    setGroupStructure(newStructure);
    setValue('interview_questions', newStructure, { shouldDirty: true });
    setIsFormChanged(true);
  };

  // Helper function to update group at path
  const updateGroupAtPath = (
    structure: InterviewQuestions,
    path: number[],
    updates: Partial<InterviewQuestions>,
    depth: number = 0,
  ): InterviewQuestions => {
    if (path.length === 0 || depth > path.length - 1) {
      return { ...structure, ...updates };
    }

    const targetIndex = path[depth];
    const childrenAsGroups = structure.children as InterviewQuestions[];
    return {
      ...structure,
      children: childrenAsGroups.map((child, idx) => {
        if (idx === targetIndex && isGroup(child)) {
          return updateGroupAtPath(child, path, updates, depth + 1);
        }
        return child;
      }),
    };
  };

  const {
    handleDragStart: dragStartHandler,
    handleDragEnd: dragEndHandler,
    handleReorderQuestionsInGroup,
    handleReorderSubgroupsInGroup,
    handleMoveQuestionBetweenGroups,
  } = useDragAndDropHandlers({
    groupStructure,
    showGroupUI,
    setGroupStructure: updateFormValue,
    setIsFormChanged,
    setReorderChanged,
  });

  const {
    handleCreateGroup,
    handleGroupNameChange,
    handleGroupRandomizeChange,
    handleGlobalRandomizeToggle,
    handleGroupDelete,
    handleAddQuestionWithOpen,
    handleAddSubgroupToGroup,
    handleDeleteQuestion: deleteQuestionHandler,
    confirmDeleteQuestion: confirmDeleteQuestionHandler,
    handleAddCheckpoint,
    handleCheckpointLabelChange,
    handleDeleteCheckpoint,
  } = useDiscussionGuideHandlers({
    groupStructure,
    singleProjectData,
    setGroupStructure: updateFormValue,
    updateGroup: (path, updates) => {
      if (path.length === 0) {
        updateFormValue({ ...groupStructure, ...updates });
      } else {
        updateFormValue(updateGroupAtPath(groupStructure, path, updates));
      }
    },
    setIsFormChanged,
    setInterviewQuestionsStructureChanged,
    setValue,
    setOpenQuestionId,
  });

  const handleAddSubgroupWithOpen = (groupIndex: number, groupPath?: number[]) => {
    const questionId = handleAddSubgroupToGroup(groupIndex, groupPath);
    if (questionId) setOpenQuestionId(questionId);
  };

  const confirmDeleteQuestion = async () => {
    await confirmDeleteQuestionHandler(
      questionToDelete,
      setIsDeletingQuestion,
      setIsDeleteModalOpen,
      setQuestionToDelete,
    );
  };

  const addQuestion = () => {
    const newQuestion: InterviewQuestion = { ...createInitialQuestion(), id: uuidv4() };

    // Ensure groupStructure has children array
    if (!groupStructure || !Array.isArray(groupStructure.children)) {
      return;
    }

    // If in group mode and a group already exists, add the question to the
    // last existing group instead of spawning a new one — "+ Add question"
    // should add a question, not a group. Creating a group is what the
    // dedicated "Create group" button (handleCreateGroup) is for.
    const lastGroupIndex = showGroupUI
      ? groupStructure.children.reduce(
          (lastIndex, child, index) => (isGroup(child) ? index : lastIndex),
          -1,
        )
      : -1;

    if (lastGroupIndex !== -1) {
      const updatedChildren = groupStructure.children.map((child, index) =>
        index === lastGroupIndex
          ? {
              ...(child as InterviewQuestions),
              children: [...(child as InterviewQuestions).children, newQuestion],
            }
          : child,
      );

      updateFormValue({
        ...groupStructure,
        children: updatedChildren as InterviewQuestions['children'],
      });
    } else {
      // Add question to root
      const existingQuestions = groupStructure.children as InterviewQuestion[];
      updateFormValue({
        ...groupStructure,
        children: [...existingQuestions, newQuestion] as InterviewQuestion[],
      });
    }

    setOpenQuestionId(newQuestion.id);
  };

  // Check if root-level randomization is enabled
  const isRootRandomized = groupStructure.randomize === true;

  const isDisabled = isUserViewer || isProjectPublished || isProjectCompleted;

  const existingGroupsCount = groupStructure?.children?.filter(isGroup).length || 0;

  // TELV1-457 — embed structure metrics. A "block" is the run of questions
  // between two checkpoints; checkpoint markers sit BETWEEN blocks. Embeds cap
  // per block (≤3 questions) and on block count (≤10 blocks / ≤9 markers), not
  // on a flat total. embedBlocks.length === checkpointCount + 1.
  const checkpointCount = groupStructure.children.filter(isCheckpoint).length;
  const embedBlocks = isEmbedProject ? splitIntoCheckpointBlocks(groupStructure.children) : [];
  const embedBlockCount = embedBlocks.length;
  const lastBlockQuestionCount = embedBlocks.length
    ? countQuestionsInBlock(embedBlocks[embedBlocks.length - 1])
    : 0;
  // "Add question" appends to the END of the list (the last block), so for embed
  // it is gated on the last block being full rather than on a global total.
  const isEmbedLastBlockFull =
    isEmbedProject && lastBlockQuestionCount >= QuestionGroupLimits.EMBED_MAX_QUESTIONS_PER_BLOCK;
  const isEmbedAtMaxLength =
    isEmbedProject &&
    embedBlockCount >= QuestionGroupLimits.EMBED_MAX_BLOCKS &&
    isEmbedLastBlockFull;
  // Just after a checkpoint is added the new block is empty: Add-question is
  // enabled but Add-checkpoint is gated until this block has a question — surface
  // that so the disabled checkpoint button is never unexplained. Only while
  // another checkpoint is still addable (below the marker cap), so the "before
  // the next checkpoint" copy never shows at the 10-block ceiling.
  const isEmbedAwaitingBlockQuestion =
    isEmbedProject &&
    checkpointCount > 0 &&
    checkpointCount < QuestionGroupLimits.EMBED_MAX_CHECKPOINTS &&
    lastBlockQuestionCount === 0;

  // Right-side hint in the embed structure counter (at most one, by priority).
  let embedHintKey: string | null = null;
  let embedHintClass = 'text-neutral-500';
  if (isEmbedAtMaxLength) {
    embedHintKey = 'msg.embedMaxLengthReached';
  } else if (isEmbedLastBlockFull) {
    embedHintKey = 'msg.embedBlockFullHint';
    embedHintClass = 'font-medium text-embed';
  } else if (isEmbedAwaitingBlockQuestion) {
    embedHintKey = 'msg.embedAddQuestionBeforeCheckpoint';
  }

  // Top-level "Add Question" button: embed gates on the last block being full;
  // interview gates on the global 40-question cap. Both respect the group limit.
  const isTopLevelAddQuestionDisabled =
    isDisabled ||
    (isEmbedProject ? isEmbedLastBlockFull : flatQuestions.length >= maxQuestions) ||
    existingGroupsCount >= QuestionGroupLimits.MAX_GROUPS ||
    !!uploadingQuestionId;

  // Group-level "Add Question" buttons (+ icons): interview-only path (embeds are
  // flat, so they never render group-level add buttons).
  const isGroupLevelAddQuestionDisabled =
    isDisabled || flatQuestions.length >= maxQuestions || !!uploadingQuestionId;

  const hasQuestions = flatQuestions.length > 0;

  // TELV1-457 — "Add checkpoint" is disabled once 9 markers (10 blocks) exist, or
  // while the current last block has no question yet — a checkpoint needs a
  // question-bearing block on both sides (the no-adjacent-empty-checkpoint rule),
  // so the researcher can never author a structure that fails to save.
  const isAddingCheckpointsDisabled =
    isDisabled ||
    checkpointCount >= QuestionGroupLimits.EMBED_MAX_CHECKPOINTS ||
    lastBlockQuestionCount === 0;

  // TELV1-421/457 — array-level / root validation messages that no individual
  // row renders: the checkpoint adjacency rule attaches to `interview_questions
  // .children` and the embed per-block / max-checkpoint rules to
  // `interview_questions` (root). Surface whichever is set as an inline banner
  // (with the `validation-error` class so a failed Save can scroll to it).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const interviewQuestionsErrors = errors?.interview_questions as any;
  const resolveCheckpointArrayErrorKey = (): string | undefined => {
    const childrenError = interviewQuestionsErrors?.children;
    if (typeof childrenError === 'string') return childrenError;
    if (typeof childrenError?.message === 'string') return childrenError.message;
    return undefined;
  };
  const rootQuestionsErrorKey =
    typeof interviewQuestionsErrors?.message === 'string'
      ? interviewQuestionsErrors.message
      : undefined;
  const structureBannerKey = resolveCheckpointArrayErrorKey() || rootQuestionsErrorKey;

  // TELV1-421 — embeds never group (defense-in-depth; the header button is also
  // hidden for embed).
  const isCreatingGroupDisabled =
    isDisabled ||
    isEmbedProject ||
    existingGroupsCount >= QuestionGroupLimits.MAX_GROUPS ||
    flatQuestions.length >= maxQuestions;

  // Group related props
  const formProps = { register, control, setValue, clearErrors, errors, submitAttempt };
  const projectProps = { isProjectPublished, isProjectCompleted, singleProjectData };
  const questionProps = {
    questions: flatQuestions,
    openQuestionId,
    setOpenQuestionId,
    fileData,
    setFileData,
    currentQuestions: currentQuestions ? flattenGroupToQuestions(currentQuestions) : [],
  };
  const groupHandlers = {
    onGroupNameChange: handleGroupNameChange,
    onGroupRandomizeChange: handleGroupRandomizeChange,
    onGroupDelete: handleGroupDelete,
    onAddQuestion: handleAddQuestionWithOpen,
    onAddSubgroup: handleAddSubgroupWithOpen,
    onReorderQuestions: handleReorderQuestionsInGroup,
    onReorderSubgroups: handleReorderSubgroupsInGroup,
    onMoveQuestionBetweenGroups: handleMoveQuestionBetweenGroups,
  };

  return (
    <>
      <div className="w-full h-full pl-1">
        <div className="flex flex-col w-full gap-6">
          {/* -- introduction (opening message) --
              TELV1-186: rendered for both project types. For embed the field is
              OPTIONAL (no required asterisk) since respondents arrive mid-survey
              and the deterministic opener leads with the first question; for
              interview it stays required. */}
          <RequiredLabel
            title="openingMessage"
            tooltip={lang.get('msg.openingMessageInfo')}
            notRequired={isEmbedProject}
          >
            <Textarea
              disabled={isUserViewer || isProjectPublished || isProjectCompleted}
              {...register('opening_message')}
              placeholder={lang.get('msg.openingMessagePlaceholder')}
              onChange={(e) => {
                setIsFormChanged(true);
                setValue('opening_message', e.target.value, { shouldDirty: true });
              }}
              className="h-24"
            />
          </RequiredLabel>

          {/* -- Interview Questions Section -- */}
          <div className="flex flex-col w-full gap-4">
            <DiscussionGuideHeader
              showGroupUI={showGroupUI}
              hasQuestions={hasQuestions}
              isRootRandomized={isRootRandomized}
              isAddingQuestionsDisabled={isTopLevelAddQuestionDisabled}
              isDisabled={isDisabled}
              isCreatingGroupDisabled={isCreatingGroupDisabled}
              currentQuestionCount={flatQuestions.length}
              maxQuestions={maxQuestions}
              isEmbedProject={isEmbedProject}
              isAddingCheckpointsDisabled={isAddingCheckpointsDisabled}
              onAddQuestion={addQuestion}
              onCreateGroup={handleCreateGroup}
              onGlobalRandomizeToggle={handleGlobalRandomizeToggle}
              onAddCheckpoint={isEmbedProject ? handleAddCheckpoint : undefined}
            />

            {/* TELV1-457: embed-only structure counter — always visible so
                researchers see the block model (≤3 questions per block, ≤10
                blocks) before they hit a limit. When the last block is full it
                hints how to continue (add a checkpoint) or, at max length, why
                they cannot. */}
            {isEmbedProject && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[12.5px] rounded-md bg-neutral-50 border border-neutral-200 border-l-2 border-l-embed">
                <span
                  className={isEmbedAtMaxLength ? 'font-medium text-embed' : 'text-neutral-600'}
                >
                  {lang
                    .get(
                      flatQuestions.length === 1
                        ? 'msg.embedStructureCounterOne'
                        : 'msg.embedStructureCounter',
                    )
                    .replace('{questions}', String(flatQuestions.length))
                    .replace('{blocks}', String(embedBlockCount))
                    .replace('{maxBlocks}', String(QuestionGroupLimits.EMBED_MAX_BLOCKS))}
                </span>
                {embedHintKey && (
                  <span className={embedHintClass}>
                    {lang
                      .get(embedHintKey)
                      .replace('{maxBlocks}', String(QuestionGroupLimits.EMBED_MAX_BLOCKS))
                      .replace(
                        '{maxPerBlock}',
                        String(QuestionGroupLimits.EMBED_MAX_QUESTIONS_PER_BLOCK),
                      )}
                  </span>
                )}
              </div>
            )}

            {/* TELV1-421 — inline banner for structural validation errors that
                no question/checkpoint row renders (checkpoint adjacency, embed
                max questions). The `validation-error` class lets a failed Save
                scroll here. */}
            {structureBannerKey && (
              <div className="validation-error flex items-center gap-2 px-3 py-2 text-sm text-red-700 border rounded-md bg-red-50 border-red-200">
                {lang.get(structureBannerKey)}
              </div>
            )}

            {/* Scrollable content area */}
            <div className="flex flex-col w-full gap-4 max-h-[calc(100vh-400px)] overflow-y-auto pr-4 pl-1">
              {/* Conditional rendering: Group UI or Flat List */}
              {showGroupUI ? (
                <GroupModeView
                  structure={{
                    groupStructure,
                    isDisabled,
                    isAddingQuestionsDisabled: isGroupLevelAddQuestionDisabled,
                  }}
                  dragAndDrop={{
                    isActiveDrag,
                    activeId,
                    onDragEnd: (event) => dragEndHandler(event, setIsActiveDrag, setActiveId),
                    onDragStart: (event) => dragStartHandler(event, setIsActiveDrag, setActiveId),
                  }}
                  handlers={{
                    ...groupHandlers,
                    onDeleteQuestion: (idToDelete) =>
                      deleteQuestionHandler(idToDelete, setQuestionToDelete, setIsDeleteModalOpen),
                    isQuestionInDatabase,
                    onCheckpointLabelChange: isEmbedProject
                      ? handleCheckpointLabelChange
                      : undefined,
                    onDeleteCheckpoint: isEmbedProject ? handleDeleteCheckpoint : undefined,
                  }}
                  formProps={formProps}
                  projectProps={projectProps}
                  questionProps={questionProps}
                />
              ) : (
                <FlatModeView
                  structure={{
                    isRootRandomized,
                    hasQuestions,
                    isDisabled,
                    isUserViewer,
                  }}
                  dragAndDrop={{
                    onDragEnd: (event) => dragEndHandler(event, setIsActiveDrag, setActiveId),
                    onDragStart: (event) => dragStartHandler(event, setIsActiveDrag, setActiveId),
                  }}
                  handlers={{
                    onDeleteQuestion: (idToDelete) =>
                      deleteQuestionHandler(idToDelete, setQuestionToDelete, setIsDeleteModalOpen),
                    isQuestionInDatabase,
                    // TELV1-421 — embed-only checkpoint editing in the flat list.
                    onCheckpointLabelChange: isEmbedProject
                      ? handleCheckpointLabelChange
                      : undefined,
                    onDeleteCheckpoint: isEmbedProject ? handleDeleteCheckpoint : undefined,
                  }}
                  rawChildren={groupStructure.children}
                  formProps={formProps}
                  projectProps={projectProps}
                  questionProps={questionProps}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteQuestionModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={confirmDeleteQuestion}
        hasMedia={
          questionToDelete
            ? !!flatQuestions.find(({ id }) => id === questionToDelete)?.media
            : false
        }
        isDeletingQuestion={isDeletingQuestion}
      />
    </>
  );
}
